/**
 * Mega menu v2.1 (GFTY-5574): the controller for sections/mega-menu.liquid.
 *
 * The menu is server-rendered; this script only shows and hides it, through the `hidden`
 * attribute and aria-expanded (WAI-ARIA disclosure navigation pattern, no role=menu):
 *  - desktop (>= 991px): an entry opens its panel on hover (with an intent delay), click,
 *    Enter or Space; ArrowDown opens it and moves into it. Escape, a click outside, leaving the
 *    menu or tabbing out closes it. A state / category with a chevron opens its drill-down on
 *    hover or focus; Space / ArrowRight also move focus into it, ArrowLeft back. A link with a
 *    chevron still navigates on click, except on a touch tap, which opens it first.
 *  - mobile: the hamburger opens the drawer (a modal dialog: focus moved in, trapped, returned,
 *    page scroll locked). An entry opens its panel over it, a chevron item its drill-down, each
 *    with a back row; everything behind the top layer is inert.
 *  - tracking: `mega-menu:panel-open` (detail { panel, level }) is dispatched on a panel
 *    (level 1) or drill-down (level 2) once per real open (GFTY-5575 contract, §2.1). Nothing
 *    here touches the dataLayer.
 *  - the sticky bar: #navbar-parent is fixed once the header has scrolled away, hidden while
 *    scrolling down (classes p-fixed / p-hide, also read by search-v2 and the PDP gallery).
 *
 * Tested in tests/mega-menu.test.js.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  } else if (root && root.document) {
    root.GiftoryMegaMenu = api;
    api.init(root.document, root);
  }
})(typeof window !== 'undefined' ? window : this, function () {
  'use strict';

  var PANEL_OPEN_EVENT = 'mega-menu:panel-open';
  // Tailwind `2md` and the DS shell breakpoint.
  var DESKTOP_QUERY = '(min-width: 991px)';
  var OPEN_DELAY = 150; // hover intent before a panel opens or switches
  var DRILL_DELAY = 100; // hover intent before a drill-down shows
  var CLOSE_DELAY = 300; // grace period after the pointer leaves the menu
  var GUTTER = 16;
  var COLUMN_WIDTH = 288; // --gf-mega-menu-column-width
  var DRILL_TRIGGER = '[aria-controls^="mm-d-"]';
  var NAV_ITEM = '.ds-mega-menu-nav-link-item, .ds-mega-menu-nav-list-item';
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /**
   * One `mega-menu:panel-open` per real open at each level: hover handlers re-run on the open
   * panel, so only a change of open panel dispatches; closing forgets it so a reopen counts.
   * `mark` records a panel shown without the user asking (the first category of a panel shown
   * by default), so it is not counted, nor counted again when the user then hovers it.
   */
  function createPanelEvents(EventCtor) {
    var open = { 1: null, 2: null };
    function nameOf(element) {
      return element && element.getAttribute ? element.getAttribute('data-mm-panel') : null;
    }
    return {
      opened: function (element, level) {
        var name = nameOf(element);
        if (!name || open[level] === name) return false;
        open[level] = name;
        if (level === 1) open[2] = null;
        element.dispatchEvent(new EventCtor(PANEL_OPEN_EVENT, { bubbles: true, detail: { panel: name, level: level } }));
        return true;
      },
      mark: function (element, level) {
        open[level] = nameOf(element);
      },
      closed: function (level) {
        if (level !== 2) open[1] = null;
        open[2] = null;
      },
    };
  }

  // Left offset of a panel under the bar (Figma "Desktop" frames): a one-column panel (the
  // compact dropdown) is aligned with its entry and kept inside the bar; a wider one is centred.
  function panelOffset(barWidth, entryLeft, panelWidth) {
    var max = barWidth - panelWidth - GUTTER;
    if (max < GUTTER || panelWidth > COLUMN_WIDTH) return Math.round((barWidth - panelWidth) / 2);
    return Math.round(Math.max(GUTTER, Math.min(entryLeft, max)));
  }

  // The pointer, moving from `from` to `to` outside `box`, heads into it: the items it crosses on
  // the way (a diagonal from the bar to a far column, or from a state to its drill-down) must not
  // switch or close what is open. The ray from `from` through `to` meets the box ahead of `to`.
  function aimsAt(from, to, box) {
    var dx = to.x - from.x;
    var dy = to.y - from.y;
    if (!dx && !dy) return false;
    if (to.x >= box.left && to.x <= box.right && to.y >= box.top && to.y <= box.bottom) return false;
    var enter = 0;
    var exit = Infinity;
    var axes = [[dx, to.x, box.left, box.right], [dy, to.y, box.top, box.bottom]];
    for (var i = 0; i < axes.length; i++) {
      var d = axes[i][0];
      var p = axes[i][1];
      if (!d) {
        if (p < axes[i][2] || p > axes[i][3]) return false;
        continue;
      }
      var a = (axes[i][2] - p) / d;
      var b = (axes[i][3] - p) / d;
      enter = Math.max(enter, Math.min(a, b));
      exit = Math.min(exit, Math.max(a, b));
    }
    return enter <= exit;
  }

  function init(doc, win) {
    var nav = doc.getElementById('mega-menu-container');
    if (!nav || nav.getAttribute('data-mm-ready')) return null;
    nav.setAttribute('data-mm-ready', '');

    var parent = doc.getElementById('navbar-parent');
    var bar = nav.querySelector('.mm-bar');
    var body = nav.querySelector('.mm-body');
    var backdrop = nav.querySelector('.mm-backdrop');
    var title = nav.querySelector('.mm-head-title');
    var closeButton = nav.querySelector('.mm-close');
    var desktop = win.matchMedia(DESKTOP_QUERY);
    var events = createPanelEvents(win.CustomEvent);

    var state = {
      entry: null, // the open entry button
      panel: null,
      openedAt: 0,
      drill: null,
      trigger: null, // the chevron item of the open drill-down
      drawer: false,
      returnFocus: null,
      pointer: 'mouse',
      keyboard: false,
      returning: null,
      inert: [],
    };
    var timers = {};

    function later(name, fn, delay) {
      win.clearTimeout(timers[name]);
      timers[name] = win.setTimeout(fn, delay);
    }
    function cancel(name) {
      win.clearTimeout(timers[name]);
    }
    function isDesktop() {
      return desktop.matches;
    }
    function labelOf(entry) {
      var label = entry.querySelector('.mm-entry-label');
      return (label ? label.textContent : entry.textContent).trim();
    }
    function visibleFocusables(scope) {
      return Array.prototype.filter.call(scope.querySelectorAll(FOCUSABLE), function (el) {
        return !el.closest('[inert], [hidden]') && el.getClientRects().length > 0;
      });
    }
    // Back to the chevron item of a drill-down just closed, without reopening it on focus.
    function focusTrigger(trigger) {
      state.returning = trigger;
      trigger.focus();
      state.returning = null;
    }
    function focusFirst(scope) {
      var first = visibleFocusables(scope)[0];
      if (first) first.focus();
    }

    // Mobile: everything but the top layer (and the drawer header) is inert.
    function isolate(layer, stop) {
      for (var el = layer; el && el !== stop; el = el.parentElement) {
        var siblings = el.parentElement ? el.parentElement.children : [];
        for (var i = 0; i < siblings.length; i++) {
          var sibling = siblings[i];
          if (sibling !== el && !sibling.inert && !sibling.hasAttribute('hidden')) {
            sibling.inert = true;
            state.inert.push(sibling);
          }
        }
      }
    }
    function release() {
      state.inert.forEach(function (el) {
        el.inert = false;
      });
      state.inert = [];
    }
    function layerChanged() {
      release();
      if (isDesktop() || !state.drawer) return;
      if (state.drill) isolate(state.drill, body);
      else if (state.panel) isolate(state.panel, body);
    }

    // ---- Drill-downs (level 2) ----------------------------------------------------------------
    function setTriggerState(trigger, open) {
      trigger.setAttribute('data-state', open ? 'active' : 'inactive');
      if (trigger.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded', String(open));
    }

    function closeDrill() {
      cancel('drill');
      if (!state.drill) return;
      state.drill.hidden = true;
      setTriggerState(state.trigger, false);
      state.panel.removeAttribute('data-drill');
      state.drill = null;
      state.trigger = null;
      events.closed(2);
    }

    function openDrill(trigger, options) {
      options = options || {};
      var drill = doc.getElementById(trigger.getAttribute('aria-controls'));
      if (!drill || !state.panel || !state.panel.contains(trigger)) return;
      if (drill !== state.drill) {
        closeDrill();
        drill.hidden = false;
        drill.scrollTop = 0;
        setTriggerState(trigger, true);
        state.panel.setAttribute('data-drill', '');
        state.drill = drill;
        state.trigger = trigger;
        layerChanged();
      }
      if (options.user) events.opened(drill, 2);
      else events.mark(drill, 2);
      if (options.focus) {
        var back = drill.querySelector('.mm-back');
        if (!isDesktop() && back) back.focus();
        else focusFirst(drill);
      }
    }

    // ---- Panels (level 1) ----------------------------------------------------------------------
    function positionPanel() {
      if (!state.panel || !isDesktop()) return;
      var barBox = bar.getBoundingClientRect();
      var entryBox = state.entry.getBoundingClientRect();
      var x = panelOffset(barBox.width, entryBox.left - barBox.left, state.panel.offsetWidth);
      state.panel.style.setProperty('--mm-x', x + 'px');
    }

    function closePanel(options) {
      options = options || {};
      cancel('entry');
      cancel('close');
      if (!state.panel) return;
      var entry = state.entry;
      closeDrill();
      entry.setAttribute('aria-expanded', 'false');
      state.panel.hidden = true;
      backdrop.hidden = true;
      state.entry = null;
      state.panel = null;
      events.closed(1);
      title.textContent = 'Menu';
      layerChanged();
      if (options.returnFocus) entry.focus();
    }

    function openPanel(entry, options) {
      options = options || {};
      cancel('entry');
      cancel('close');
      if (state.entry === entry) {
        if (options.focus === 'first') focusFirst(state.panel);
        return;
      }
      closePanel();
      var panel = doc.getElementById(entry.getAttribute('aria-controls'));
      if (!panel) return;
      state.entry = entry;
      state.panel = panel;
      state.openedAt = Date.now();
      entry.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      panel.scrollTop = 0;
      if (isDesktop()) {
        backdrop.hidden = false;
        positionPanel();
        // Experience Type, Gift Ideas: the first category's links show until another is chosen.
        var first = panel.querySelector('[data-drills] .mm-drill') && panel.querySelector('[data-column="leading"] ' + DRILL_TRIGGER);
        if (first) openDrill(first);
      } else {
        title.textContent = labelOf(entry);
        layerChanged();
      }
      events.opened(panel, 1);
      if (options.focus === 'first') focusFirst(panel);
      else if (options.focus === 'back') panel.querySelector('.mm-back').focus();
    }

    // ---- Mobile drawer -----------------------------------------------------------------------------
    function openDrawer(returnFocus) {
      if (state.drawer) return;
      state.drawer = true;
      state.returnFocus = returnFocus || null;
      parent.classList.add('menu-visible');
      parent.setAttribute('role', 'dialog');
      parent.setAttribute('aria-modal', 'true');
      parent.setAttribute('aria-labelledby', title.id);
      nav.setAttribute('data-drawer', '');
      doc.documentElement.classList.add('mm-locked');
      body.scrollTop = 0;
      setHamburgers(true);
    }

    function closeDrawer(options) {
      options = options || {};
      closePanel();
      if (!state.drawer) return;
      state.drawer = false;
      release();
      parent.classList.remove('menu-visible');
      parent.removeAttribute('role');
      parent.removeAttribute('aria-modal');
      parent.removeAttribute('aria-labelledby');
      nav.removeAttribute('data-drawer');
      doc.documentElement.classList.remove('mm-locked');
      setHamburgers(false);
      var target = state.returnFocus;
      state.returnFocus = null;
      if (target && options.returnFocus !== false && target.getClientRects().length) target.focus();
    }

    function setHamburgers(open) {
      Array.prototype.forEach.call(doc.querySelectorAll('.hamburger-btn'), function (button) {
        button.setAttribute('aria-expanded', String(open));
      });
    }

    function openSearch() {
      closeAll();
      if (typeof win.handleMobileSearchClick === 'function') win.handleMobileSearchClick();
    }

    function closeAll() {
      if (state.drawer) closeDrawer({ returnFocus: false });
      else closePanel();
    }

    // ---- Pointer -------------------------------------------------------------------------------------
    doc.addEventListener('pointerdown', function (event) {
      state.pointer = event.pointerType || 'mouse';
    }, true);
    // While the keyboard is in use, hover is ignored until the mouse really moves: scrolling a
    // column under a resting pointer fires mouseover, which must not close the panel.
    var mouseX = -1;
    var mouseY = -1;
    var trail = []; // the last few pointer positions, for aimsAt
    doc.addEventListener('mousemove', function (event) {
      if (event.clientX === mouseX && event.clientY === mouseY) return;
      mouseX = event.clientX;
      mouseY = event.clientY;
      trail.push({ x: mouseX, y: mouseY, t: Date.now() });
      if (trail.length > 4) trail.shift();
      state.keyboard = false;
    }, { passive: true });

    // A pointer at rest aims at nothing: the item under it takes over as usual.
    function aimingAt(element) {
      var last = trail[trail.length - 1];
      if (!element || !last || Date.now() - last.t > OPEN_DELAY) return false;
      var from = trail.filter(function (point) { return last.t - point.t <= OPEN_DELAY; })[0];
      return aimsAt(from, last, element.getBoundingClientRect());
    }

    // Desktop hover: one delegated listener; the target says where the pointer is.
    doc.addEventListener('mouseover', function (event) {
      hover(event.target);
    });

    function hover(target) {
      if (!target || !isDesktop() || state.pointer === 'touch' || state.keyboard) return;
      var item = target.closest && target.closest('.mm-item');
      var inPanel = state.panel && state.panel.contains(target);
      // On the way to the open panel: check again once the pointer has moved on or stopped.
      if (!inPanel && aimingAt(state.panel)) {
        cancel('close');
        later('entry', function () { hover(doc.elementFromPoint(mouseX, mouseY)); }, OPEN_DELAY);
        return;
      }
      if (!item || !nav.contains(item)) {
        cancel('entry');
        if (state.panel) later('close', closePanel, CLOSE_DELAY);
        return;
      }
      cancel('close');
      if (inPanel) {
        cancel('entry');
        // On the way from a state to its open drill-down (e.g. to "View all Arizona").
        if (state.drill && !state.drill.contains(target) && aimingAt(state.drill)) {
          later('drill', function () { hover(doc.elementFromPoint(mouseX, mouseY)); }, DRILL_DELAY);
          return;
        }
        var trigger = target.closest(DRILL_TRIGGER);
        if (trigger) {
          if (trigger !== state.trigger) later('drill', function () { openDrill(trigger, { user: true }); }, DRILL_DELAY);
          else cancel('drill');
        } else if (state.panel.getAttribute('data-kind') === 'location' && target.closest(NAV_ITEM) && !target.closest('.mm-drill')) {
          // A state without cities brings the pods back.
          later('drill', closeDrill, DRILL_DELAY);
        }
        return;
      }
      var entry = item.querySelector('.mm-entry');
      if (entry === state.entry) {
        cancel('entry');
      } else if (entry.hasAttribute('aria-controls')) {
        later('entry', function () { openPanel(entry); }, OPEN_DELAY);
      } else if (state.panel) {
        later('entry', closePanel, OPEN_DELAY);
      }
    }

    // ---- Clicks -----------------------------------------------------------------------------------------
    doc.addEventListener('click', function (event) {
      var target = event.target;
      if (!target.closest) return;

      var hamburger = target.closest('.hamburger-btn');
      if (hamburger) {
        openDrawer(hamburger);
        closeButton.focus();
        return;
      }
      if (!nav.contains(target)) {
        if (state.panel && isDesktop()) closePanel();
        else if (state.drawer) closeDrawer();
        return;
      }
      // The drawer's search field hands over to the search overlay when it is used, not when
      // it gets focus: tabbing through the drawer must not close it.
      if (target.closest('[data-mm-search]')) {
        openSearch();
        return;
      }
      if (target.closest('[data-mm-close]')) {
        if (state.drawer) closeDrawer();
        else closePanel();
        return;
      }
      var back = target.closest('[data-mm-back]');
      if (back) {
        if (state.drill && state.drill.contains(back)) {
          var trigger = state.trigger;
          closeDrill();
          layerChanged();
          focusTrigger(trigger);
        } else {
          closePanel({ returnFocus: true });
        }
        return;
      }
      var entry = target.closest('.mm-entry[aria-controls]');
      if (entry) {
        if (!isDesktop()) {
          if (!state.drawer) openDrawer(entry);
          openPanel(entry, { focus: 'back' });
        } else if (state.entry !== entry) {
          openPanel(entry);
        } else if (event.detail === 0 || state.pointer !== 'mouse' || Date.now() - state.openedAt > 600) {
          // A mouse click right after the hover opened it keeps it open.
          closePanel();
        }
        return;
      }
      var drillTrigger = target.closest(DRILL_TRIGGER);
      if (drillTrigger && state.panel && state.panel.contains(drillTrigger)) {
        var isLink = drillTrigger.tagName === 'A';
        var tap = !isDesktop() || state.pointer === 'touch';
        if (!isLink || (tap && state.trigger !== drillTrigger)) {
          event.preventDefault();
          openDrill(drillTrigger, { user: true, focus: !isDesktop() });
        }
      }
    });

    // ---- Keyboard -------------------------------------------------------------------------------------
    // Typing in the drawer's search field (keyboard users) also hands over to the overlay.
    doc.addEventListener('input', function (event) {
      if (event.target.matches && event.target.matches('[data-mm-search]')) openSearch();
    });

    doc.addEventListener('keydown', function (event) {
      var target = event.target;
      var key = event.key;
      state.keyboard = true;
      cancel('close');

      if (key === 'Escape') {
        if (state.drill && (!isDesktop() || state.drill.contains(target))) {
          var trigger = state.trigger;
          closeDrill();
          layerChanged();
          focusTrigger(trigger);
        } else if (state.panel) {
          closePanel({ returnFocus: true });
        } else if (state.drawer) {
          closeDrawer();
        }
        return;
      }
      if (!nav.contains(target)) return;

      if (key === 'Tab' && state.drawer && !isDesktop()) {
        var focusables = visibleFocusables(parent);
        if (!focusables.length) return;
        var first = focusables[0];
        var last = focusables[focusables.length - 1];
        if (focusables.indexOf(target) === -1 || (event.shiftKey && target === first)) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus();
        } else if (!event.shiftKey && target === last) {
          event.preventDefault();
          first.focus();
        }
        return;
      }
      if (key === 'ArrowDown' && isDesktop() && target.matches('.mm-entry[aria-controls]')) {
        event.preventDefault();
        openPanel(target, { focus: 'first' });
        return;
      }
      var drillTrigger = target.closest(DRILL_TRIGGER);
      if (drillTrigger && !target.matches('.mm-entry') && (key === ' ' || key === 'ArrowRight')) {
        event.preventDefault();
        openDrill(drillTrigger, { user: true, focus: true });
        return;
      }
      if (key === 'ArrowLeft' && state.drill && state.drill.contains(target) && isDesktop()) {
        event.preventDefault();
        focusTrigger(state.trigger);
      }
    });

    // Desktop: focusing a chevron item shows its drill-down, like hovering it; tabbing out of
    // the open entry and its panel closes it.
    doc.addEventListener('focusin', function (event) {
      var target = event.target;
      if (!isDesktop() || !state.panel) return;
      var item = state.entry.closest('.mm-item');
      if (!item.contains(target)) {
        closePanel();
        return;
      }
      var trigger = target.closest(DRILL_TRIGGER);
      if (trigger && trigger !== state.returning && state.panel.contains(trigger) && trigger !== state.trigger) openDrill(trigger, { user: true });
    });

    // Leaving the breakpoint resets the menu; resizing keeps an open panel under its entry.
    var onBreakpoint = function () {
      closeAll();
    };
    if (desktop.addEventListener) desktop.addEventListener('change', onBreakpoint);
    else if (desktop.addListener) desktop.addListener(onBreakpoint);
    win.addEventListener('resize', function () {
      later('resize', positionPanel, 100);
    });

    initStickyBar(doc, win, parent, function () {
      return Boolean(state.panel || state.drawer);
    });

    return { closeAll: closeAll, state: state };
  }

  /**
   * Sticky bar, as before GFTY-5574: once the site header has scrolled out of view the bar (and
   * the header's mobile search row) is fixed, hidden while scrolling down and shown again while
   * scrolling up; the code banner follows. Off on the mobile cart page, and frozen while the
   * menu is open.
   */
  function initStickyBar(doc, win, navbar, isMenuOpen) {
    var searchRow = doc.getElementById('mobile-search-row');
    var siteHeader = doc.querySelector('.site-header');
    var codeBanner = doc.querySelector('.code-banner');
    var bars = [navbar, searchRow].filter(Boolean);
    var THRESHOLD = 5;
    var lastScrollTop = 0;
    var fixed = false;
    var ticking = false;

    function toggle(name, on) {
      bars.forEach(function (el) {
        el.classList.toggle(name, on);
      });
    }
    function banner(sticky, top) {
      if (!codeBanner) return;
      if (sticky !== null) codeBanner.classList.toggle('sticky', sticky);
      if (top !== null) codeBanner.style.top = top;
    }

    if (!siteHeader) return;
    if (win.location.pathname === '/cart' && !win.matchMedia(DESKTOP_QUERY).matches) {
      toggle('p-fixed', false);
      toggle('p-hide', false);
      banner(false, '0px');
      return;
    }

    function onScroll() {
      ticking = false;
      var current = win.pageYOffset || doc.documentElement.scrollTop;
      if (Math.abs(current - lastScrollTop) < THRESHOLD) return;
      var headerVisible = siteHeader.getBoundingClientRect().bottom > 0;

      if (headerVisible) {
        toggle('p-fixed', false);
        toggle('p-hide', false);
        banner(false, '0px');
        fixed = false;
      } else if (!isMenuOpen()) {
        if (!fixed) {
          toggle('p-fixed', true);
          fixed = true;
        }
        var down = current > lastScrollTop;
        toggle('p-hide', down);
        if (down) banner(true, '0px');
        else banner(null, '55px');
      }
      lastScrollTop = Math.max(current, 0);
    }

    win.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      win.requestAnimationFrame(onScroll);
    }, { passive: true });
  }

  return {
    PANEL_OPEN_EVENT: PANEL_OPEN_EVENT,
    DESKTOP_QUERY: DESKTOP_QUERY,
    createPanelEvents: createPanelEvents,
    panelOffset: panelOffset,
    aimsAt: aimsAt,
    init: init,
  };
});
